import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Row, Col, Card, Button, Form, Table,
  Badge, Spinner, Nav
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  ArcElement, CategoryScale, LinearScale,
  BarElement, LineElement, PointElement,
  Title, Tooltip, Legend
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import { useTrainingAuth } from '../../context/TrainingAuthContext';
import TrainingNavbar from '../../components/TrainingNavbar';
import trainingService from '../../services/trainingService';
import usePagination from '../../hooks/usePagination';
import TablePagination from '../../components/TablePagination';

ChartJS.register(
  ArcElement, CategoryScale, LinearScale,
  BarElement, LineElement, PointElement,
  Title, Tooltip, Legend
);

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);

const PALETTE = [
  '#4472C4','#ED7D31','#A9D18E','#FF4B4B','#FFC000',
  '#5B9BD5','#70AD47','#9467BD','#FF7F7F','#8FAADC',
  '#FFD966','#FF99CC','#BDD7EE','#C9C9C9','#C00000',
];
const ADNOC_COLOR    = '#0d6efd';
const NONADNOC_COLOR = '#fd7e14';
const CHART_H        = 320;

function KpiCard({ label, value, bg = 'primary', text = 'white' }) {
  return (
    <Card className="text-center mb-3" bg={bg} text={text}>
      <Card.Body className="py-3">
        <div className="small fw-semibold">{label}</div>
        <h3 className="mb-0 fw-bold">{value ?? '—'}</h3>
      </Card.Body>
    </Card>
  );
}

function ChartCard({ title, height = CHART_H, children }) {
  return (
    <Card className="h-100">
      <Card.Header className="fw-semibold">{title}</Card.Header>
      <Card.Body><div style={{ height }}>{children}</div></Card.Body>
    </Card>
  );
}

function LoadingBox() {
  return (
    <div className="text-center py-5">
      <Spinner animation="border" variant="success" />
      <p className="mt-2 text-muted">Loading…</p>
    </div>
  );
}

function NoData() {
  return <p className="text-center text-muted py-4">No training data found for this period.</p>;
}

const stackedOpts = {
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { position: 'bottom' } },
  scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true } }
};
const groupedOpts = {
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { position: 'bottom' } },
  scales: { y: { beginAtZero: true } }
};
const pieOpts = {
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { position: 'right' } }
};

function buildSeriesDataset(seriesArr, months) {
  return {
    labels: months,
    datasets: seriesArr.map((s, i) => ({
      label: s.name,
      data: s.monthly,
      backgroundColor: PALETTE[i % PALETTE.length] + 'CC',
      borderColor: PALETTE[i % PALETTE.length],
      borderWidth: 1,
    })),
  };
}

// ── Tab 1: Monthly Training ──────────────────────────────────────────────────
function MonthlyTrainingTab({ year }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [groupBy, setGroupBy] = useState('course');

  useEffect(() => { setData(null); }, [year]);
  useEffect(() => {
    if (data || loading) return;
    setLoading(true);
    trainingService.getTrainingMonthly(year)
      .then(res => { if (res.success) setData(res); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [year, data, loading]);

  const { months, monthly, summary, by_trainer, by_course, by_client } = data || {};

  const seriesMap = { trainer: by_trainer, course: by_course, client: by_client };
  const series = seriesMap[groupBy] || [];
  const { page: seriesPage, setPage: setSeriesPage, totalPages: seriesTotalPages, paginatedItems: paginatedSeries } = usePagination(series);

  if (loading) return <LoadingBox />;
  if (!data) return <NoData />;

  const adnocChart = {
    labels: months,
    datasets: [
      { label: 'ADNOC', data: monthly.map(m => m.adnoc), backgroundColor: ADNOC_COLOR + 'CC', borderColor: ADNOC_COLOR, borderWidth: 1 },
      { label: 'Non-ADNOC', data: monthly.map(m => m.non_adnoc), backgroundColor: NONADNOC_COLOR + 'CC', borderColor: NONADNOC_COLOR, borderWidth: 1 },
    ],
  };

  const seriesChart = series.length > 0 ? buildSeriesDataset(series, months) : null;

  const splitPie = {
    labels: ['ADNOC', 'Non-ADNOC'],
    datasets: [{ data: [summary.adnoc, summary.non_adnoc], backgroundColor: [ADNOC_COLOR + 'CC', NONADNOC_COLOR + 'CC'], borderColor: [ADNOC_COLOR, NONADNOC_COLOR], borderWidth: 1 }],
  };

  return (
    <>
      <Row className="mb-3">
        <Col md={3}><KpiCard label={`Total Certificates ${year}`} value={summary.total} bg="dark" /></Col>
        <Col md={3}><KpiCard label="ADNOC" value={summary.adnoc} bg="primary" /></Col>
        <Col md={3}><KpiCard label="Non-ADNOC" value={summary.non_adnoc} bg="warning" text="dark" /></Col>
        <Col md={3}><KpiCard label="ADNOC %" value={summary.total ? Math.round(summary.adnoc / summary.total * 100) + '%' : '0%'} bg="info" /></Col>
      </Row>
      <Row className="mb-4">
        <Col md={8}>
          <ChartCard title={`Monthly Training Certificates — ADNOC vs Non-ADNOC (${year})`}>
            <Bar data={adnocChart} options={stackedOpts} />
          </ChartCard>
        </Col>
        <Col md={4}>
          <ChartCard title="ADNOC / Non-ADNOC Split" height={280}>
            <Pie data={splitPie} options={pieOpts} />
          </ChartCard>
        </Col>
      </Row>
      <Row className="mb-2 align-items-center">
        <Col><h6 className="mb-0 fw-semibold">Breakdown by:</h6></Col>
        <Col xs="auto">
          <Form.Select value={groupBy} onChange={e => { setGroupBy(e.target.value); setSeriesPage(1); }} style={{ width: 160 }}>
            <option value="course">Course</option>
            <option value="trainer">Instructor</option>
            <option value="client">Client / Company</option>
          </Form.Select>
        </Col>
      </Row>
      <Row className="mb-4">
        <Col md={12}>
          {seriesChart
            ? <ChartCard title={`Monthly — by ${groupBy.charAt(0).toUpperCase() + groupBy.slice(1)} (stacked)`}>
                <Bar data={seriesChart} options={stackedOpts} />
              </ChartCard>
            : <NoData />}
        </Col>
      </Row>
      {series.length > 0 && (
        <Card className="mb-4">
          <Card.Header className="fw-semibold">Summary by {groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}</Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table striped bordered hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>{groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}</th>
                    {months.map(m => <th key={m}>{m}</th>)}
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSeries.map((s, i) => (
                    <tr key={i}>
                      <td>{s.name}</td>
                      {s.monthly.map((v, j) => <td key={j}>{v || ''}</td>)}
                      <td><strong>{s.total}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <TablePagination currentPage={seriesPage} totalPages={seriesTotalPages} onPageChange={setSeriesPage} />
            </div>
          </Card.Body>
        </Card>
      )}
      <Card>
        <Card.Header className="fw-semibold">Monthly Totals — {year}</Card.Header>
        <Card.Body>
          <Table striped bordered size="sm" className="mb-0">
            <thead><tr><th>Month</th><th>ADNOC</th><th>Non-ADNOC</th><th>Total</th></tr></thead>
            <tbody>
              {monthly.map((m, i) => (
                <tr key={i}>
                  <td>{m.month_name}</td>
                  <td><Badge bg="primary">{m.adnoc}</Badge></td>
                  <td><Badge bg="warning" text="dark">{m.non_adnoc}</Badge></td>
                  <td><strong>{m.total}</strong></td>
                </tr>
              ))}
              <tr className="table-dark">
                <td><strong>Total</strong></td>
                <td><strong>{summary.adnoc}</strong></td>
                <td><strong>{summary.non_adnoc}</strong></td>
                <td><strong>{summary.total}</strong></td>
              </tr>
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </>
  );
}

// ── Tab 2: Instructor Utilization ────────────────────────────────────────────
function InstructorUtilisationTab({ year }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setData(null); }, [year]);
  useEffect(() => {
    if (data || loading) return;
    setLoading(true);
    trainingService.getTrainingUtilization(year)
      .then(res => { if (res.success) setData(res); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [year, data, loading]);

  const { months, trainers, years, yearly_data } = data || {};

  const { page: trainersPage, setPage: setTrainersPage, totalPages: trainersTotalPages, paginatedItems: paginatedTrainers } = usePagination(trainers || []);
  const { page: yearlyPage, setPage: setYearlyPage, totalPages: yearlyTotalPages, paginatedItems: paginatedYearlyData } = usePagination(yearly_data || []);

  if (loading) return <LoadingBox />;
  if (!data) return <NoData />;

  const monthlyChart = trainers.length > 0 ? buildSeriesDataset(trainers, months) : null;
  const yearlyChart = yearly_data.length > 0 && years.length > 0 ? {
    labels: years.map(String),
    datasets: yearly_data.map((s, i) => ({
      label: s.name, data: s.yearly,
      backgroundColor: PALETTE[i % PALETTE.length] + 'CC',
      borderColor: PALETTE[i % PALETTE.length], borderWidth: 1,
    })),
  } : null;

  const trainerPie = trainers.length > 0 ? {
    labels: trainers.map(t => t.name),
    datasets: [{
      data: trainers.map(t => t.total),
      backgroundColor: trainers.map((_, i) => PALETTE[i % PALETTE.length] + 'CC'),
      borderColor: trainers.map((_, i) => PALETTE[i % PALETTE.length]),
      borderWidth: 1,
    }],
  } : null;

  const totalCerts = trainers.reduce((a, t) => a + t.total, 0);

  return (
    <>
      <Row className="mb-3">
        <Col md={3}><KpiCard label="Active Instructors" value={trainers.length} bg="success" /></Col>
        <Col md={3}><KpiCard label={`Total Certificates ${year}`} value={totalCerts} bg="primary" /></Col>
        <Col md={3}>
          {trainers.length > 0 && <KpiCard label="Most Active Instructor" value={trainers[0].name} bg="info" />}
        </Col>
      </Row>
      <Row className="mb-4">
        <Col md={8}>
          {monthlyChart
            ? <ChartCard title={`Monthly Instructor Utilization — ${year} (stacked)`}>
                <Bar data={monthlyChart} options={stackedOpts} />
              </ChartCard>
            : <NoData />}
        </Col>
        <Col md={4}>
          {trainerPie
            ? <ChartCard title={`Instructor Share — ${year}`} height={280}>
                <Pie data={trainerPie} options={pieOpts} />
              </ChartCard>
            : <NoData />}
        </Col>
      </Row>
      {yearlyChart && (
        <Row className="mb-4">
          <Col>
            <ChartCard title="Annual Instructor Utilization (last 5 years)">
              <Bar data={yearlyChart} options={groupedOpts} />
            </ChartCard>
          </Col>
        </Row>
      )}
      <Card>
        <Card.Header className="fw-semibold">Instructor Details — {year}</Card.Header>
        <Card.Body>
          <div className="table-responsive">
            <Table striped bordered hover size="sm" className="mb-0">
              <thead>
                <tr><th>Instructor</th>{months.map(m => <th key={m}>{m}</th>)}<th>Total</th></tr>
              </thead>
              <tbody>
                {paginatedTrainers.map((t, i) => (
                  <tr key={i}><td>{t.name}</td>{t.monthly.map((v, j) => <td key={j}>{v || ''}</td>)}<td><strong>{t.total}</strong></td></tr>
                ))}
              </tbody>
            </Table>
            <TablePagination currentPage={trainersPage} totalPages={trainersTotalPages} onPageChange={setTrainersPage} />
          </div>
        </Card.Body>
      </Card>
      {yearly_data.length > 0 && years.length > 0 && (
        <Card className="mt-4">
          <Card.Header className="fw-semibold">Annual Instructor Detail</Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table striped bordered hover size="sm" className="mb-0">
                <thead>
                  <tr><th>Instructor</th>{years.map(y => <th key={y}>{y}</th>)}<th>Total</th></tr>
                </thead>
                <tbody>
                  {paginatedYearlyData.map((t, i) => (
                    <tr key={i}><td>{t.name}</td>{t.yearly.map((v, j) => <td key={j}>{v || ''}</td>)}<td><strong>{t.total}</strong></td></tr>
                  ))}
                </tbody>
              </Table>
              <TablePagination currentPage={yearlyPage} totalPages={yearlyTotalPages} onPageChange={setYearlyPage} />
            </div>
          </Card.Body>
        </Card>
      )}
    </>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'monthly',     label: 'Monthly Training' },
  { key: 'utilisation', label: 'Instructor Utilization' },
];

const TrainingReportsPage = () => {
  const navigate = useNavigate();
  const { trainingUser } = useTrainingAuth();
  const [activeTab, setActiveTab] = useState('monthly');
  const [year, setYear] = useState(CURRENT_YEAR);

  const handleYearChange = useCallback((y) => setYear(y), []);

  const dashboardPath = ['training_coordinator', 'training_document_controller'].includes(trainingUser?.role)
    ? '/training/coordinator'
    : '/training/reviewer';

  return (
    <>
      <TrainingNavbar />
      <Container fluid className="mt-3 mb-5 px-4">
        <Row className="mb-3 align-items-center">
          <Col>
            <h3 className="mb-0">
              <i className="fas fa-graduation-cap me-2 text-success"></i>
              Training — Reports &amp; Analytics
            </h3>
          </Col>
          <Col xs="auto" className="d-flex gap-2 align-items-center">
            <Form.Group className="d-flex align-items-center gap-2 mb-0">
              <Form.Label className="mb-0 fw-semibold">Year:</Form.Label>
              <Form.Select value={year} onChange={e => handleYearChange(Number(e.target.value))} style={{ width: 110 }}>
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </Form.Select>
            </Form.Group>
            <Button variant="outline-secondary" size="sm" onClick={() => navigate(dashboardPath)}>
              <i className="fas fa-arrow-left me-1"></i> Back
            </Button>
          </Col>
        </Row>

        <Nav variant="tabs" activeKey={activeTab} onSelect={k => setActiveTab(k)} className="mb-3">
          {TABS.map(t => (
            <Nav.Item key={t.key}>
              <Nav.Link eventKey={t.key}>{t.label}</Nav.Link>
            </Nav.Item>
          ))}
        </Nav>

        <div>
          {activeTab === 'monthly'     && <MonthlyTrainingTab year={year} />}
          {activeTab === 'utilisation' && <InstructorUtilisationTab year={year} />}
        </div>
      </Container>
    </>
  );
};

export default TrainingReportsPage;
